/**
 * @fileoverview Match3 Grid Component
 * @description 8x8 grid of gem tiles
 */

import React from "react";
import { View, StyleSheet, useWindowDimensions } from "react-native";
import { Match3Tile as TileType } from "../types";
import { Match3Tile } from "./Match3Tile";
import { useThemeColors } from "@/providers/ThemeProvider";

interface Match3GridProps {
  grid: TileType[][];
  onTilePress: (row: number, col: number) => void;
  disabled?: boolean;
}

/**
 * 8x8 grid component for the Match3 game
 */
export const Match3Grid: React.FC<Match3GridProps> = ({
  grid,
  onTilePress,
  disabled = false,
}) => {
  const colors = useThemeColors();
  const { width: screenWidth } = useWindowDimensions();

  // Calculate tile size based on screen width
  // Grid has 8 columns, with padding on sides
  const gridPadding = 16;
  const gridWidth = Math.min(screenWidth - gridPadding * 2, 400);
  const tileSize = Math.floor(gridWidth / 8);
  const actualGridWidth = tileSize * 8;

  if (!grid || grid.length === 0) {
    return null;
  }

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.surface,
          borderColor: colors.border,
          width: actualGridWidth + 8,
          padding: 4,
        },
      ]}
    >
      <View style={styles.gridBackground}>
        {grid.map((row, rowIndex) => (
          <View key={`row-${rowIndex}`} style={styles.row}>
            {row.map((tile, colIndex) => (
              <Match3Tile
                key={tile.id}
                tile={tile}
                size={tileSize}
                onPress={() => onTilePress(rowIndex, colIndex)}
                disabled={disabled}
              />
            ))}
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    borderWidth: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
  },
  gridBackground: {
    overflow: "hidden",
    borderRadius: 8,
  },
  row: {
    flexDirection: "row",
  },
});

export default Match3Grid;

